import {Injectable} from '@angular/core';
import {DcpEntryGroup} from '@dcp/configurator';
import {UntilDestroy} from '@ngneat/until-destroy';

import {OrderEntry} from '@spartacus/cart/base/root';
import {EntryGroupService} from '@app/product/services/entry-group.service';
import {DcpProductUtilitiesService} from '@app/product/services/dcp-product-utilities.service';
import {EddlAdobeTrackingDataProductData} from './models/eddl-adobe-tracking-data';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DcpEddlCartStateService {
  constructor(
    private readonly dcpProductUtilitiesService: DcpProductUtilitiesService,
    protected entryGroupService: EntryGroupService
  ) {}

  getRelevantCpsGroups(entryGroups: DcpEntryGroup[], productCode: string): DcpEntryGroup[] {
    // Includes PRODUCT groups on purpose: the main product's OrderEntry lives in the PRODUCT
    // child group of the ROOT group, so filtering it out drops the main product from EDDL
    // events (DCP-61177). Matches getRelevantLegacyGroups.
    const relevantRootEntryGroups = this.entryGroupService.getRootGroupsByProductCode(entryGroups, productCode);
    return relevantRootEntryGroups.reduce((acc, rootEntryGroup) => [...acc, ...(rootEntryGroup.entryGroups ?? [])], []);
  }

  getRelevantLegacyGroups(entryGroups: DcpEntryGroup[], productCode: string): DcpEntryGroup[] {
    const relevantRootEntryGroups = this.entryGroupService.getRootGroupsByProductCode(entryGroups, productCode);
    return relevantRootEntryGroups.reduce((acc, rootEntryGroup) => [...acc, ...(rootEntryGroup.entryGroups ?? [])], []);
  }

  getRelevantCartEntries(entries: OrderEntry[]): OrderEntry[] {
    return entries?.filter(
      (entry: OrderEntry) =>
        !(!entry.product || !entry.totalPrice || Object.keys(entry.product).includes('configuratorType'))
    );
  }

  getProductTrackingData(entry: OrderEntry): EddlAdobeTrackingDataProductData {
    if (!entry) {
      return;
    }
    // Keep the product even when the formatted id is empty, mirroring the orderComplete
    // path (dcp-eddl-ecommerce-data.service.ts getRelevantProducts). Dropping on empty id
    // here while orderComplete keeps it made cartAdd under-count vs orderComplete and pushed
    // the Adobe conversion rate above 100% for upsell products (DCP-61998 / DCP-61514).
    const id = this.dcpProductUtilitiesService.getFormattedEntitlementRuleCode(entry.product);
    const product: EddlAdobeTrackingDataProductData = {
      id,
      name: entry.product?.name,
      quantity: entry.quantity?.toString(),
      price: entry.basePrice?.value?.toString(),
    };
    // Products are only assigned to one category. They can belong to multiple industries but not categories (DCP-20673).
    if (entry.product?.categories && entry.product.categories[0]?.name) {
      product.category = entry.product.categories[0].name;
    }
    if (entry.product?.vendor?.name) {
      product.partnerName = entry.product.vendor.name;
    }
    return product;
  }
}
